// Typed client for POST /api/smart-search-chat, the conversational Smart Search
// used by the clothing sidebar (scope 'clothing') and, later, the printing browser
// (scope 'printing'). Callers own the AbortSignal; a 20 s timeout is added here.

import { API_ORIGIN } from './apiBase';
import { SORT_OPTIONS, SortKey } from '../data/clothing/browseConfig';

const ENDPOINT = `${API_ORIGIN}/api/smart-search-chat`;
const TIMEOUT_MS = 20_000;

export interface SmartSearchTurn {
  role: 'user' | 'assistant';
  content: string;
}

/** Same shape as the page's BrowseFilters, minus `page`, with null for "no bound". */
export interface ClothingFilters {
  types: string[];
  genders: string[];
  ages: string[];
  colours: string[];
  brands: string[];
  sizes: string[];
  min: number | null;
  max: number | null;
  eco: boolean;
  q: string;
  sort: SortKey;
}

export interface ClothingSearchRequest {
  messages: SmartSearchTurn[];
  current: ClothingFilters;
}

export interface ClothingSearchResult {
  reply: string;
  /** null = leave the page as it is, just show the reply. */
  filters: ClothingFilters | null;
  total: number | null;
  relaxed: string[];
}

export interface PrintingSearchProduct {
  slug: string;
  title: string;
  description: string;
  categories: string[];
}

export interface PrintingSearchCategory {
  id: string;
  label: string;
}

export interface PrintingSearchRequest {
  messages: SmartSearchTurn[];
  products: PrintingSearchProduct[];
  categories: PrintingSearchCategory[];
}

export interface PrintingSearchResult {
  reply: string;
  category: string | null;
  picks: string[];
}

export class SmartSearchError extends Error {
  readonly status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = 'SmartSearchError';
    this.status = status;
  }
}

async function post<T>(body: unknown, signal?: AbortSignal): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, TIMEOUT_MS);
  const forwardAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', forwardAbort, { once: true });
  }

  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!res.ok) throw new SmartSearchError(`Smart search failed (${res.status})`, res.status);
    return (await res.json()) as T;
  } catch (err) {
    // A timeout surfaces as an AbortError; report it as a real failure, not a cancel.
    if (timedOut) throw new SmartSearchError('Smart search timed out');
    throw err;
  } finally {
    window.clearTimeout(timer);
    signal?.removeEventListener('abort', forwardAbort);
  }
}

const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : []);
const numberOrNull = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

function normaliseFilters(raw: unknown): ClothingFilters | null {
  if (!raw || typeof raw !== 'object') return null;
  const f = raw as Record<string, unknown>;
  const sort = SORT_OPTIONS.some((o) => o.value === f.sort) ? (f.sort as SortKey) : 'popular';
  return {
    types: strings(f.types),
    genders: strings(f.genders),
    ages: strings(f.ages),
    colours: strings(f.colours),
    brands: strings(f.brands),
    sizes: strings(f.sizes),
    min: numberOrNull(f.min),
    max: numberOrNull(f.max),
    eco: f.eco === true,
    q: typeof f.q === 'string' ? f.q : '',
    sort,
  };
}

export async function askClothingSearch(req: ClothingSearchRequest, signal?: AbortSignal): Promise<ClothingSearchResult> {
  const data = await post<Record<string, unknown>>({ scope: 'clothing', messages: req.messages, current: req.current }, signal);
  if (typeof data?.reply !== 'string') throw new SmartSearchError('Smart search returned an unexpected response');
  return {
    reply: data.reply,
    filters: normaliseFilters(data.filters),
    total: numberOrNull(data.total),
    relaxed: strings(data.relaxed),
  };
}

export async function askPrintingSearch(req: PrintingSearchRequest, signal?: AbortSignal): Promise<PrintingSearchResult> {
  const data = await post<Record<string, unknown>>(
    { scope: 'printing', messages: req.messages, products: req.products, categories: req.categories },
    signal,
  );
  if (typeof data?.reply !== 'string') throw new SmartSearchError('Smart search returned an unexpected response');
  return {
    reply: data.reply,
    category: typeof data.category === 'string' ? data.category : null,
    picks: strings(data.picks),
  };
}
